import { useEffect, useState } from 'react'
import api from '../../../lib/api'
import type { User, UserCreate } from '../../../types'
import { UserRole } from '../../../types'
import { authService } from '../services/auth'
import { ROLE_LABELS } from '../../../lib/constants'
import { UserPlus, Users, CheckCircle, XCircle, AlertCircle, ChevronDown, KeyRound, Trash2, Pencil } from 'lucide-react'
import PasswordInput from '../../../components/PasswordInput'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Policy maps (mirror backend ROLE_CREATION_POLICY) ───────────────────────

const CREATABLE_ROLES: Partial<Record<UserRole, UserRole[]>> = {
  [UserRole.DEALER_PRINCIPAL]: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.TELECALLER],
  [UserRole.INSURANCE_HEAD]:   [UserRole.TEAM_LEADER, UserRole.TELECALLER],
  [UserRole.TEAM_LEADER]:      [UserRole.TELECALLER],
}

// Which role tabs each persona sees
const VISIBLE_ROLES: Partial<Record<UserRole, UserRole[]>> = {
  [UserRole.DEALER_PRINCIPAL]: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.TELECALLER],
  [UserRole.INSURANCE_HEAD]:   [UserRole.TEAM_LEADER, UserRole.TELECALLER],
  [UserRole.TEAM_LEADER]:      [UserRole.TELECALLER],
}

const TAB_LABEL: Partial<Record<UserRole, string>> = {
  [UserRole.INSURANCE_HEAD]: 'Insurance Heads',
  [UserRole.TEAM_LEADER]:    'Team Leaders',
  [UserRole.TELECALLER]:     'Telecallers',
}

const singularLabel = (role: UserRole) =>
  TAB_LABEL[role]?.replace(/s$/, '') ?? ROLE_LABELS[role] ?? role

// ── Helpers ──────────────────────────────────────────────────────────────────

const emptyForm = (): UserCreate => ({
  email: '', mobile: '', full_name: '', password: '',
  role: UserRole.TELECALLER, language: 'en',
})

// ── Stat Card ────────────────────────────────────────────────────────────────

function RoleStatCard({
  role, total, active, selected, onClick,
}: {
  role: UserRole
  total: number
  active: number
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="card text-left w-full transition-all"
      style={{
        borderColor: selected ? 'var(--brand-primary)' : 'var(--border-default)',
        boxShadow: selected ? '0 0 0 2px var(--brand-primary-subtle)' : undefined,
      }}
    >
      <p
        className="text-xs font-semibold uppercase tracking-wider mb-2"
        style={{ color: selected ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
      >
        {TAB_LABEL[role]}
      </p>
      <p
        className="text-3xl font-bold tabular-nums"
        style={{ color: selected ? 'var(--brand-primary)' : 'var(--text-primary)' }}
      >
        {total}
      </p>
      <p className="text-xs mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
        {active} active · {total - active} inactive
      </p>
    </button>
  )
}

// ── Add-member form ──────────────────────────────────────────────────────────

function AddMemberForm({
  role,
  onSubmit,
  onCancel,
}: {
  role: UserRole
  onSubmit: (form: UserCreate) => Promise<void>
  onCancel: () => void
}) {
  const [form, setForm] = useState<UserCreate>({ ...emptyForm(), role })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const set = (field: keyof UserCreate, value: unknown) =>
    setForm(prev => ({ ...prev, [field]: value }))

  const setErrorField = (field: string, msg: string | null) =>
    setErrors(prev => {
      if (msg === null) {
        const { [field]: _, ...rest } = prev; return rest
      }
      return { ...prev, [field]: msg }
    })

  const handleChange = (field: keyof UserCreate, raw: string) => {
    let val: string | number = raw
    if (field === 'mobile') val = raw.replace(/[^+\d]/g, '')
    set(field, val)
  }

  const handleBlur = (field: keyof UserCreate) => {
    const val = form[field]
    if (typeof val !== 'string') return
    if (field === 'mobile') {
      const digits = val.replace(/\D/g, '')
      if (digits && (digits.length < 10 || digits.length > 15)) {
        setErrorField('mobile', 'Mobile must be 10-15 digits')
      } else {
        setErrorField('mobile', null)
      }
    }
    if (field === 'email') {
      if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        setErrorField('email', 'Enter a valid email address')
      } else {
        setErrorField('email', null)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.password.length < 12) { setError('Password must be at least 12 characters'); return }
    // Validate mobile and email
    const newErrors: Record<string, string> = {}
    const mobileStr = (form.mobile as string) || ''
    const emailStr = (form.email as string) || ''
    const digits = mobileStr.replace(/\D/g, '')
    if (mobileStr && (digits.length < 10 || digits.length > 15)) newErrors.mobile = 'Mobile must be 10-15 digits'
    if (emailStr && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr)) newErrors.email = 'Enter a valid email address'
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return
    setSubmitting(true)
    setError('')
    try {
      await onSubmit(form)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to create user')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="p-5 space-y-4"
      style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--surface-subtle)' }}
      autoComplete="off"
    >
      <h3
        className="text-sm font-semibold flex items-center gap-2"
        style={{ color: 'var(--text-primary)' }}
      >
        <UserPlus className="h-4 w-4" />
        New {singularLabel(role)}
      </h3>

      {error && (
        <div className="alert-danger">
          <XCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className="form-label">Full Name <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <input
            type="text" className="input" required
            value={form.full_name} onChange={e => handleChange('full_name', e.target.value)}
            placeholder="Priya Sharma"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Mobile <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <input
            type="tel" className="input" required
            value={form.mobile} onChange={e => handleChange('mobile', e.target.value)}
            onBlur={() => handleBlur('mobile')}
            placeholder="9876543210"
          />
          {errors.mobile && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.mobile}</p>}
        </div>
        <div className="form-group">
          <label className="form-label">Email <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <input
            type="email" className="input" required
            value={form.email} onChange={e => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            placeholder="priya@dealership.com"
          />
          {errors.email && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.email}</p>}
        </div>
        <div className="form-group">
          <label className="form-label">Password <span style={{ color: 'var(--danger-text)' }}>*</span></label>
          <PasswordInput
            value={form.password} onChange={e => set('password', e.target.value)}
            placeholder="Min. 12 characters" minLength={12} required
            autoComplete="new-password"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Language</label>
          <select className="input" value={form.language} onChange={e => set('language', e.target.value)}>
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="ta">Tamil</option>
            <option value="te">Telugu</option>
            <option value="kn">Kannada</option>
            <option value="mr">Marathi</option>
            <option value="gu">Gujarati</option>
            <option value="bn">Bengali</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : `Add ${singularLabel(role)}`}
        </button>
      </div>
    </form>
  )
}

// ── Reset Password Modal ─────────────────────────────────────────────────────

function ResetPasswordModal({
  user,
  onClose,
  onSuccess,
}: {
  user: User
  onClose: () => void
  onSuccess: (msg: string) => void
}) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 12) { setError('Password must be at least 12 characters'); return }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return }
    setSubmitting(true)
    setError('')
    try {
      await api.patch(`/admin/users/${user.id}`, { new_password: newPassword })
      onSuccess(`Password reset successfully for ${user.full_name}.`)
      onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to reset password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <KeyRound className="h-4 w-4" />
            Reset Password
          </h2>
          <button className="btn-icon" onClick={onClose}><XCircle className="h-4 w-4" /></button>
        </div>
        <div className="p-4">
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Set a new password for <strong>{user.full_name}</strong>.
          </p>
          {error && (
            <div className="alert-danger mb-4">
              <XCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="form-label">New Password <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <PasswordInput
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 12 characters"
                minLength={12}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <PasswordInput
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                minLength={12}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// ── Edit Member Modal ─────────────────────────────────────────────────────────

function EditMemberModal({
  user,
  onClose,
  onSuccess,
}: {
  user: User
  onClose: () => void
  onSuccess: (msg: string) => void
}) {
  const [fullName, setFullName] = useState(user.full_name)
  const [email,    setEmail]    = useState(user.email)
  const [mobile,   setMobile]   = useState(user.mobile)
  const [submitting, setSubmitting] = useState(false)
  const [error,    setError]    = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const payload: Record<string, string> = {}
    if (fullName.trim() !== user.full_name) payload.full_name = fullName.trim()
    if (email.trim()    !== user.email)     payload.email     = email.trim()
    if (mobile.trim()   !== user.mobile)    payload.mobile    = mobile.trim()
    if (Object.keys(payload).length === 0) { onClose(); return }
    setSubmitting(true)
    try {
      await api.patch(`/admin/users/${user.id}`, payload)
      onSuccess(`${fullName.trim()} updated successfully.`)
      onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to update profile')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Pencil className="h-4 w-4" />
            Edit Profile
          </h2>
          <button className="btn-icon" onClick={onClose}><XCircle className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-4">
            {error && (
              <div className="alert-danger">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Full Name <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input
                className="input"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                required
                minLength={1}
                autoComplete="name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile <span style={{ color: 'var(--danger-text)' }}>*</span></label>
              <input
                className="input"
                type="tel"
                value={mobile}
                onChange={e => setMobile(e.target.value)}
                required
                autoComplete="tel"
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Remove Team Member Modal ──────────────────────────────────────────────────
// On confirm: tries hard-delete first. If backend returns 409 (user has records),
// automatically falls back to deactivation. The user just clicks "Remove".

function DeleteUserModal({
  user,
  onClose,
  onSuccess,
}: {
  user: User
  onClose: () => void
  onSuccess: (msg: string) => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState('')

  const handleConfirm = async () => {
    setSubmitting(true)
    setError('')
    try {
      await api.delete(`/admin/users/${user.id}`)
      onSuccess('')
      onClose()
    } catch (deleteErr: unknown) {
      const status = (deleteErr as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        try {
          await api.patch(`/admin/users/${user.id}`, { is_active: false })
          onSuccess('')
          onClose()
        } catch (patchErr: unknown) {
          const detail = (patchErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          setError(detail || 'Failed to deactivate user')
        }
      } else {
        const detail = (deleteErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setError(detail || 'Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--danger-text)' }}>
            <Trash2 className="h-4 w-4" />
            Remove team member
          </h2>
          <button className="btn-icon" onClick={onClose}><XCircle className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
            Are you sure you want to remove <strong>{user.full_name}</strong> from the team?
            Any leads currently assigned to them will be set to unassigned.
          </p>

          {error && (
            <div className="alert-danger">
              <XCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{ background: 'var(--danger-text)', borderColor: 'var(--danger-text)' }}
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function TeamPage() {
  const currentUser = authService.getCurrentUser() as User
  const myRole = (currentUser?.role ?? '') as UserRole

  const creatableRoles = CREATABLE_ROLES[myRole] ?? []
  const visibleRoles   = VISIBLE_ROLES[myRole]   ?? []

  const [users, setUsers]           = useState<User[]>([])
  const [loading, setLoading]       = useState(true)
  const [activeTab, setActiveTab]   = useState<UserRole>(visibleRoles[0] ?? UserRole.TELECALLER)
  const [showForm, setShowForm]     = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [toggleError, setToggleError] = useState('')
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)
  const [deleteUser,        setDeleteUser]        = useState<User | null>(null)
  const [editUser,          setEditUser]          = useState<User | null>(null)


  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/users')
      setUsers(res.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  // Scoped to own tenant (BA is handled separately)
  const tenantUsers = users.filter(u => u.tenant_id === currentUser?.tenant_id)

  const byRole = (role: UserRole) => tenantUsers.filter(u => u.role === role)
  const activeCount = (role: UserRole) => byRole(role).filter(u => u.is_active).length

  const isMobile = useWindowWidth() < 768
  const tabUsers = byRole(activeTab)
  const canAddOnTab = creatableRoles.includes(activeTab)

  const switchTab = (role: UserRole) => {
    setActiveTab(role)
    setShowForm(false)
    setSuccessMsg('')
  }

  const handleCreate = async (form: UserCreate) => {
    await api.post('/admin/users', form)
    setSuccessMsg(`${singularLabel(form.role as UserRole)} "${form.full_name}" added successfully.`)
    setShowForm(false)
    await fetchUsers()
  }

  const handleToggleActive = async (user: User) => {
    try {
      await api.patch(`/admin/users/${user.id}`, { is_active: !user.is_active })
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: !u.is_active } : u))
    } catch {
      setToggleError('Failed to update user status.')
    }
  }

  // ── Guard: BA uses Admin Panel; TC has no team ─────────────────────────────

  if (myRole === UserRole.BACKEND_ADMIN) {
    return (
      <div className="empty-state">
        <Users className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>Use Admin Panel</p>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Cross-tenant user management is in the Admin Panel.
        </p>
      </div>
    )
  }

  if (visibleRoles.length === 0) {
    return (
      <div className="empty-state">
        <Users className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p style={{ color: 'var(--text-tertiary)' }}>No team management access.</p>
      </div>
    )
  }

  const pageSubtitle: Record<string, string> = {
    [UserRole.DEALER_PRINCIPAL]: 'Manage your full team — click a card to drill in',
    [UserRole.INSURANCE_HEAD]:   'Manage your Team Leaders and Telecallers',
    [UserRole.TEAM_LEADER]:      'Manage your Telecallers',
  }

  const handleActionSuccess = async (msg: string) => {
    if (msg) setSuccessMsg(msg)
    await fetchUsers()
  }

  return (
    <div className="space-y-6">
      {editUser && (
        <EditMemberModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSuccess={async msg => { setEditUser(null); await handleActionSuccess(msg) }}
        />
      )}
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onSuccess={msg => { setResetPasswordUser(null); setSuccessMsg(msg) }}
        />
      )}
      {deleteUser && (
        <DeleteUserModal
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onSuccess={async msg => { setDeleteUser(null); await handleActionSuccess(msg) }}
        />
      )}

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Users className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            Team
          </h1>
          <p className="page-subtitle">{pageSubtitle[myRole] ?? ''}</p>
        </div>
      </div>

      {/* ── Stat cards (only when 2+ role tabs) ───────────────────────────── */}
      {visibleRoles.length > 1 && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${visibleRoles.length}, minmax(0, 1fr))` }}
        >
          {visibleRoles.map(role => (
            <RoleStatCard
              key={role}
              role={role}
              total={loading ? 0 : byRole(role).length}
              active={loading ? 0 : activeCount(role)}
              selected={activeTab === role}
              onClick={() => switchTab(role)}
            />
          ))}
        </div>
      )}

      {/* ── Alerts ────────────────────────────────────────────────────────── */}
      {successMsg && (
        <div className="alert-success">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span>{successMsg}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setSuccessMsg('')}>
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}
      {toggleError && (
        <div className="alert-danger">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{toggleError}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setToggleError('')}>
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Table card ────────────────────────────────────────────────────── */}
      <div className="table-card">

        {/* Tab bar + Add button */}
        <div
          className="flex items-center justify-between px-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          {/* Role tabs */}
          <div className="flex">
            {visibleRoles.map(role => (
              <button
                key={role}
                onClick={() => switchTab(role)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors"
                style={{
                  borderBottomColor: activeTab === role ? 'var(--brand-primary)' : 'transparent',
                  color: activeTab === role ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                }}
              >
                {TAB_LABEL[role]}
                {!loading && (
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                    style={{
                      background: activeTab === role ? 'var(--brand-primary-subtle)' : 'var(--surface-subtle)',
                      color: activeTab === role ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                    }}
                  >
                    {byRole(role).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Add button — only if this tab's role is creatable */}
          {canAddOnTab && (
            <button
              className="btn-primary"
              style={{ height: 30, fontSize: 12 }}
              onClick={() => { setShowForm(v => !v); setSuccessMsg('') }}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Add {singularLabel(activeTab)}
            </button>
          )}
        </div>

        {/* Inline add form */}
        {showForm && canAddOnTab && (
          <AddMemberForm
            role={activeTab}
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
          />
        )}

        {/* User list */}
        {loading ? (
          <div className="py-12 text-center"><div className="spinner mx-auto" /></div>
        ) : tabUsers.length === 0 ? (
          <div className="py-10 text-center" style={{ color: 'var(--text-tertiary)' }}>
            No {TAB_LABEL[activeTab]?.toLowerCase()} yet.
            {canAddOnTab && (
              <button className="ml-1.5 font-medium" style={{ color: 'var(--brand-primary)' }} onClick={() => setShowForm(true)}>
                Add one →
              </button>
            )}
          </div>
        ) : isMobile ? (
          <div className="flex flex-col gap-2 p-3">
            {tabUsers.map(u => {
              const expanded = expandedUserId === u.id
              return (
                <div key={u.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div
                    className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                    onClick={() => setExpandedUserId(expanded ? null : u.id)}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                      style={{ background: 'var(--brand-50)', color: 'var(--brand-700)' }}
                    >
                      {u.full_name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{u.full_name}</p>
                      <span className={`text-xs ${u.is_active ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: 10 }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <ChevronDown
                      className="h-4 w-4 shrink-0 transition-transform"
                      style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                    />
                  </div>
                  {expanded && (
                    <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Email</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.email}</span>
                      </div>
                      {u.mobile && (
                        <div className="flex justify-between text-xs py-1">
                          <span style={{ color: 'var(--text-tertiary)' }}>Mobile</span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.mobile}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Last login</span>
                        <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>
                          {u.last_login_at
                            ? new Date(u.last_login_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'Never'}
                        </span>
                      </div>
                      {u.id !== currentUser?.id && (
                        <div className="flex flex-col gap-2 mt-3">
                          <button
                            className="w-full text-xs font-medium py-2 rounded-md border"
                            style={{
                              borderColor: u.is_active ? 'var(--danger-text)' : 'var(--success-text)',
                              color: u.is_active ? 'var(--danger-text)' : 'var(--success-text)',
                              background: 'transparent',
                            }}
                            onClick={e => { e.stopPropagation(); handleToggleActive(u) }}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="w-full text-xs font-medium py-2 rounded-md border flex items-center justify-center gap-1.5"
                            style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'transparent' }}
                            onClick={e => { e.stopPropagation(); setEditUser(u) }}
                          >
                            <Pencil className="h-3 w-3" /> Edit Profile
                          </button>
                          <button
                            className="w-full text-xs font-medium py-2 rounded-md border flex items-center justify-center gap-1.5"
                            style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'transparent' }}
                            onClick={e => { e.stopPropagation(); setResetPasswordUser(u) }}
                          >
                            <KeyRound className="h-3 w-3" /> Reset Password
                          </button>
                          <button
                            className="w-full text-xs font-medium py-2 rounded-md border flex items-center justify-center gap-1.5"
                            style={{ borderColor: 'var(--danger-text)', color: 'var(--danger-text)', background: 'transparent' }}
                            onClick={e => { e.stopPropagation(); setDeleteUser(u) }}
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tabUsers.map(u => (
                  <tr key={u.id}>
                    <td className="text-primary-data">{u.full_name}</td>
                    <td style={{ fontSize: 12 }}>{u.email}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{u.mobile}</td>
                    <td>
                      <span className={u.is_active ? 'badge-success' : 'badge-danger'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Never'}
                    </td>
                    <td>
                      {u.id !== currentUser?.id && (
                        <div className="flex items-center gap-3">
                          <button
                            className="text-xs font-medium transition-colors"
                            style={{ color: u.is_active ? 'var(--danger-text)' : 'var(--success-text)' }}
                            onClick={() => handleToggleActive(u)}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn-icon"
                            title="Edit profile"
                            onClick={() => setEditUser(u)}
                          >
                            <Pencil className="h-3.5 w-3.5" style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          <button
                            className="btn-icon"
                            title="Reset password"
                            onClick={() => setResetPasswordUser(u)}
                          >
                            <KeyRound className="h-3.5 w-3.5" style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          <button
                            className="btn-icon"
                            title="Delete user"
                            onClick={() => setDeleteUser(u)}
                          >
                            <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
